<script setup>
import { computed, ref, toRef, watch } from 'vue';
import { useStore } from 'vuex';
import { getLastMessage } from 'dashboard/helper/conversationHelper';
import Avatar from 'next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import MessagePreview from './MessagePreview.vue';
import TimeAgo from 'dashboard/components/ui/TimeAgo.vue';
import CardLabels from './conversationCardComponents/CardLabels.vue';
import CardPriorityIcon from 'dashboard/components-next/Conversation/ConversationCard/CardPriorityIcon.vue';
import UnreadBadge from 'dashboard/components-next/Conversation/ConversationCard/UnreadBadge.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import VoiceCallStatus from './VoiceCallStatus.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import PanelIaStateIndicator from './PanelIaStateIndicator.vue';
import CaptainStateBadge from './CaptainStateBadge.vue';
import { useCaptainState } from 'dashboard/composables/useCaptainState';
import CampaignCardBadge from 'dashboard/components-next/Conversation/ConversationCard/CampaignCardBadge.vue';

const props = defineProps({
  chat: { type: Object, required: true },
  currentContact: { type: Object, required: true },
  assignee: { type: Object, default: () => ({}) },
  inbox: { type: Object, default: () => ({}) },
  selected: { type: Boolean, default: false },
  isActiveChat: { type: Boolean, default: false },
  showAssignee: { type: Boolean, default: false },
  hideThumbnail: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
});

const emit = defineEmits([
  'click',
  'contextmenu',
  'selectConversation',
  'deSelectConversation',
]);

const hovered = ref(false);
const store = useStore();
const typingUsers = computed(() =>
  store.getters['conversationTypingStatus/getUserList'](props.chat.id)
);

const {
  showAssistantAvatar,
  assistant: captainAssistant,
  ringClass: captainRingClass,
  tooltip: captainTooltip,
  isEscalated: isCaptainEscalated,
} = useCaptainState(toRef(props, 'chat'), typingUsers);

const unreadCount = computed(() => props.chat.unread_count);
const hasUnread = computed(() => unreadCount.value > 0);
const lastMessageInChat = computed(() => getLastMessage(props.chat));

const voiceCallData = computed(() => {
  const last = lastMessageInChat.value;
  if (last?.content_type !== 'voice_call' || !last.call) {
    return { status: null, direction: null };
  }
  return {
    status: last.call.status,
    direction: last.call.direction === 'outgoing' ? 'outbound' : 'inbound',
  };
});

const campaignMeta = computed(() => props.chat.meta?.campaign);

const channelInbox = computed(() =>
  props.inbox?.channel_type ? props.inbox : null
);

const isAIAssignee = computed(() =>
  ['AgentBot', 'Captain::Assistant'].includes(props.chat?.meta?.assignee_type)
);

// While Captain answers, its avatar (with the state ring) already tells who owns the conversation.
const isCaptainAssignee = computed(
  () => props.chat?.meta?.assignee_type === 'Captain::Assistant'
);

const showAssigneeAvatar = computed(
  () =>
    props.showAssignee &&
    !!props.assignee?.name &&
    !(showAssistantAvatar.value && isCaptainAssignee.value)
);

const assigneeAvatarIcon = computed(() => {
  if (isAIAssignee.value && !props.assignee?.thumbnail) {
    return 'i-lucide-bot';
  }
  return null;
});

const hasSlaPolicyId = computed(
  () => props.chat?.applied_sla?.id && !props.currentContact?.blocked
);

const showLabelsSection = computed(() => {
  return props.chat.labels?.length > 0 || hasSlaPolicyId.value;
});

const messagePreviewClass = computed(() => {
  return [
    hasUnread.value ? 'font-medium text-n-slate-12' : 'text-n-slate-11',
    !props.compact && hasUnread.value ? 'ltr:pr-4 rtl:pl-4' : '',
    props.compact && hasUnread.value ? 'ltr:pr-6 rtl:pl-6' : '',
  ];
});

const onThumbnailHover = () => {
  hovered.value = !props.hideThumbnail;
};

const onThumbnailLeave = () => {
  hovered.value = false;
};

const onSelectConversation = checked => {
  if (checked) {
    emit('selectConversation', props.chat.id, props.inbox.id);
  } else {
    emit('deSelectConversation', props.chat.id, props.inbox.id);
  }
};

const selectedModel = computed({
  get: () => props.selected,
  set: value => onSelectConversation(value),
});

watch(
  () => props.chat.id,
  () => {
    hovered.value = false;
  }
);
</script>

<template>
  <div
    class="relative flex items-start flex-grow-0 flex-shrink-0 w-auto max-w-full py-0 cursor-pointer conversation border-b border-n-slate-3 transition-colors duration-200 ease-out hover:border-n-surface-1 hover:bg-n-alpha-2 dark:hover:bg-n-alpha-3 group hover:z-[1] before:content-[none] before:absolute before:-top-px before:inset-x-0 before:h-px before:bg-n-surface-1 before:pointer-events-none hover:before:content-['']"
    :class="{
      'active animate-card-select bg-n-brand/10 !border-n-surface-1 border-l-4 !border-l-n-brand':
        isActiveChat,
      'selected bg-n-blue-3/70 dark:bg-n-blue-3/30 !border-n-surface-1':
        selected,
      'px-0': compact,
      'px-3': !compact,
    }"
    @click="$emit('click', $event)"
    @contextmenu="$emit('contextmenu', $event)"
  >
    <PanelIaStateIndicator :chat="chat" />
    <div
      class="relative"
      @mouseenter="onThumbnailHover"
      @mouseleave="onThumbnailLeave"
    >
      <Avatar
        v-if="!hideThumbnail"
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="32"
        :status="currentContact.availability_status"
        :inbox="channelInbox"
        :badge-ratio="0.48"
        class="mt-4"
        rounded-full
        use-brand-icon
        hide-offline-status
      >
        <template #overlay="{ size }">
          <label
            v-if="hovered || selected"
            class="flex items-center justify-center rounded-full cursor-pointer absolute inset-0 z-10 backdrop-blur-[2px]"
            :style="{ width: `${size}px`, height: `${size}px` }"
            @click.stop
          >
            <Checkbox
              v-model="selectedModel"
              :aria-label="
                $t('GENERAL.ACCESSIBILITY.SELECT_CONVERSATION', {
                  name: currentContact.name,
                })
              "
            />
          </label>
        </template>
      </Avatar>
    </div>
    <div class="px-0 py-3 flex-1 min-w-0 border-line">
      <h4
        class="conversation--user text-sm my-0 mx-2 capitalize pt-0.5 flex items-center gap-1 min-w-0 ltr:pr-20 rtl:pl-20 transition-colors duration-200"
        :class="
          hasUnread
            ? 'font-semibold text-n-slate-12'
            : 'font-medium text-n-slate-12'
        "
      >
        <span
          v-if="showAssistantAvatar"
          v-tooltip.top="{
            content: captainTooltip,
            delay: { show: 500, hide: 0 },
          }"
          class="inline-flex flex-shrink-0 rounded-full ring-2"
          :class="captainRingClass"
          data-testid="captain-assistant-avatar"
        >
          <Avatar
            :name="captainAssistant.name"
            :src="captainAssistant.thumbnail"
            :size="16"
            rounded-full
            hide-offline-status
          />
        </span>
        <Icon
          v-if="showAssistantAvatar"
          icon="i-lucide-chevron-right"
          class="size-3 text-n-slate-10 flex-shrink-0"
        />
        <Avatar
          v-if="showAssigneeAvatar"
          v-tooltip.top="{
            content: assignee.name,
            delay: { show: 500, hide: 0 },
          }"
          :name="assignee.name"
          :src="assignee.thumbnail"
          :icon-name="assigneeAvatarIcon"
          :size="16"
          :status="assignee.availability_status"
          rounded-full
          hide-offline-status
          class="flex-shrink-0"
        />
        <Icon
          v-if="showAssigneeAvatar"
          icon="i-lucide-chevron-right"
          class="size-3 text-n-slate-10 flex-shrink-0"
        />
        <span class="truncate">{{ currentContact.name }}</span>
      </h4>
      <VoiceCallStatus
        v-if="voiceCallData.status"
        key="voice-status-row"
        :status="voiceCallData.status"
        :direction="voiceCallData.direction"
        :message-preview-class="messagePreviewClass"
      />
      <MessagePreview
        v-else-if="lastMessageInChat"
        key="message-preview"
        :message="lastMessageInChat"
        class="my-0 mx-2 leading-6 h-6 flex-1 min-w-0 text-sm"
        :class="messagePreviewClass"
      />
      <p
        v-else
        key="no-messages"
        class="text-n-slate-11 text-sm my-0 mx-2 leading-6 h-6 flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap"
        :class="messagePreviewClass"
      >
        <fluent-icon
          size="16"
          class="-mt-0.5 align-middle inline-block text-n-slate-10"
          icon="info"
        />
        <span class="mx-0.5">
          {{ $t(`CHAT_LIST.NO_MESSAGES`) }}
        </span>
      </p>
      <div
        class="absolute flex flex-col items-end ltr:right-3 rtl:left-3 top-4"
      >
        <div class="flex items-center gap-1">
          <CardPriorityIcon
            v-if="chat.priority"
            :priority="chat.priority"
            class="flex-shrink-0 !size-3.5"
          />
          <span class="font-normal leading-4 text-xxs">
            <TimeAgo
              :last-activity-timestamp="chat.timestamp"
              :created-at-timestamp="chat.created_at"
              :conversation-id="chat.id"
            />
          </span>
        </div>
        <UnreadBadge
          v-if="hasUnread"
          :count="unreadCount"
          class="ltr:ml-auto rtl:mr-auto mt-1"
        />
      </div>
      <div v-if="isCaptainEscalated" class="mt-0.5 mx-2 mb-0">
        <CaptainStateBadge :chat="chat" />
      </div>
      <div v-if="campaignMeta?.title" class="mt-0.5 mx-2 mb-0">
        <CampaignCardBadge
          variant="row"
          :title="campaignMeta.title"
          :display-id="campaignMeta.id"
          :color="campaignMeta.color"
        />
      </div>
      <CardLabels
        v-if="showLabelsSection"
        :conversation-labels="chat.labels"
        class="mt-0.5 mx-2 mb-0"
      >
        <template v-if="hasSlaPolicyId" #before>
          <SLACardLabel :chat="chat" class="ltr:mr-1 rtl:ml-1" />
        </template>
      </CardLabels>
    </div>
  </div>
</template>
